import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Percent, Tag, Flame, Clock, Truck, Star } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealsPage = () => {
  const navigate = useNavigate();
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        // Fetch products with discounts (where original_price > price)
        const response = await axios.get(`${API}/products`, { params: { limit: 1000 } });
        const dealsProducts = (response.data || []).filter(
          p => p.original_price && p.original_price > p.price
        );
        // Sort by discount percentage
        dealsProducts.sort((a, b) => {
          const discountA = ((a.original_price - a.price) / a.original_price) * 100;
          const discountB = ((b.original_price - b.price) / b.original_price) * 100;
          return discountB - discountA;
        });
        setProducts(dealsProducts);
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, []);

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    if (product.stock <= 0) {
      toast.error('Out of stock');
      return;
    }
    addToCart(product);
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pb-20">
        <div className="bg-gradient-to-r from-[#f57224] to-[#ff9a5c] px-4 py-6">
          <h1 className="text-xl font-bold text-white">Deals & Offers</h1>
        </div>
        <div className="p-4 space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={`deals-skeleton-${i}`} className="bg-white rounded-xl p-4 animate-pulse flex gap-4">
              <div className="w-24 h-24 bg-slate-200 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-200 rounded w-1/2" />
                <div className="h-5 bg-slate-200 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#f57224] to-[#ff9a5c] px-4 py-6 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <Percent className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Deals & Offers</h1>
            <p className="text-sm text-white/80">{products.length} products on sale</p>
          </div>
        </div>
        
        {/* Quick stats */}
        <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 flex-shrink-0">
            <Flame className="w-4 h-4 text-white" />
            <span className="text-xs text-white font-medium">Up to 50% OFF</span>
          </div>
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 flex-shrink-0">
            <Truck className="w-4 h-4 text-white" />
            <span className="text-xs text-white font-medium">Free Delivery</span>
          </div>
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1.5 flex-shrink-0">
            <Clock className="w-4 h-4 text-white" />
            <span className="text-xs text-white font-medium">Limited Time</span>
          </div>
        </div>
      </div>

      {/* Deals List */}
      <div className="p-4 space-y-3">
        {products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Tag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No deals available right now</p>
            <p className="text-sm text-slate-400 mt-1">Check back later for amazing offers!</p>
          </div>
        ) : (
          products.map((product, index) => {
            const discountPercent = Math.round(
              ((product.original_price - product.price) / product.original_price) * 100
            );
            const qty = getItemQuantity(product.id);
            
            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/product/${product.id}`)}
                className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden flex cursor-pointer active:scale-[0.99] transition-transform"
                data-testid={`deal-${product.id}`}
              >
                {/* Image */}
                <div className="relative w-28 h-28 flex-shrink-0 bg-slate-50">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Discount badge */}
                  <div className="absolute top-2 left-2 bg-[#f57224] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                    {discountPercent}% OFF
                  </div>
                </div>
                
                {/* Details */}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                  <div>
                    {product.brand && (
                      <p className="text-[10px] text-slate-400 uppercase font-medium truncate">
                        {product.brand}
                      </p>
                    )}
                    <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 leading-tight">
                      {product.name}
                    </h3>
                    
                    {/* Rating */}
                    {product.avg_rating > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        <span className="text-xs text-slate-600">{product.avg_rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-end justify-between mt-2">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-base font-bold text-slate-900">
                          ₹{product.price.toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-400 line-through">
                          ₹{product.original_price.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-[10px] text-green-600 font-medium">
                        Save ₹{(product.original_price - product.price).toLocaleString()}
                      </p>
                    </div>
                    
                    {/* Add button */}
                    {qty > 0 ? (
                      <div 
                        className="flex items-center bg-[#0047AB] rounded-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => updateQuantity(product.id, qty - 1)}
                          className="w-7 h-8 flex items-center justify-center text-white hover:bg-white/20"
                        >
                          -
                        </button>
                        <span className="w-6 text-center text-white font-bold text-sm">{qty}</span>
                        <button
                          onClick={() => qty < product.stock && addToCart(product)}
                          className="w-7 h-8 flex items-center justify-center text-white hover:bg-white/20"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => handleAddToCart(product, e)}
                        disabled={product.stock <= 0}
                        className="bg-[#0047AB] text-white text-xs font-semibold px-4 py-2 rounded-lg hover:bg-[#003380] disabled:bg-slate-300 transition-colors"
                      >
                        {product.stock <= 0 ? 'Out' : 'Add'}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DealsPage;
